import React, { useContext, useEffect, useState } from "react";
import { Box, createStyles, makeStyles, Theme, useTheme } from "@material-ui/core";
import { TestRequestAdvancedSearchRQ } from "../../api/models/advanced-search/TestRequestAdvancedSearchRQ";
import AdvancedSearch from "./AdvancedSearch";
import { ADVANCED_SEARCH_INITIAL, AdvancedSearchForm, formatAdvancedSearchObject } from "./AdvancedSearch.utils";
import {
  getSearchObjectAdvancedArchive,
  resetSearchObjectAdvancedArchive,
  setSearchObjectAdvancedArchive,
  SortDirection,
} from "../../helper/UserSettingsHelper";
import CustomButton from "../../components/Button/CustomButton";
import GenericPermissionContext from "../../contexts/GenericPermissionContext";
import useEnv from "../../hooks/env";
import AdvancedSearchApi from "../../api/services/AdvancedSearchApi";
import ErrorSnackbarContext from "../../contexts/ErrorSnackbarContext";
import { TestRequestSortColumn } from "../Dashboard/TestRequestsTable/TestRequestsOverviewColumns";
import CustomFooterGraphic from "../Requests/CustomFooterGraphic";
import ExcelExportDialog from "./ExcelExportDialog";
import TestRequestExportSnapshotResponse from "../../api/models/advanced-search/TestRequestExportSnapshotResponse";
import TestRequestsResultView from "../Dashboard/TestRequestsResultView";
import ViewMode from "../../models/ViewMode";

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      margin: theme.spacing(3),
    },
    withButton: {
      display: "inline-block",
    },
    exportResult: {
      float: "right",
      marginTop: theme.spacing(2),
    },
  }),
);

const SearchPage = () => {
  const [advancedSearch, setAdvancedSearch] = useState<TestRequestAdvancedSearchRQ>(
    getSearchObjectAdvancedArchive() || ADVANCED_SEARCH_INITIAL,
  );
  const { api } = useEnv();
  const theme = useTheme();
  const advancedSearchApi = new AdvancedSearchApi(api);
  const { handleHttpError } = useContext(ErrorSnackbarContext);
  const { exportArchivedSearch } = useContext(GenericPermissionContext);
  const classes = useStyles();
  const [exportDialogOpen, setExportDialogOpen] = useState(false);
  const [hasResults, setHasResults] = useState(false);
  const [sortColumn, setSortColumn] = useState<TestRequestSortColumn>("SORTING_DATE");
  const [sortDirection, setSortDirection] = useState<SortDirection>("desc");
  const [excelConfiguratorTableOpen, setExcelConfiguratorTableOpen] = useState(false);
  const [snapshotObj, setSnapshotObj] = useState<TestRequestExportSnapshotResponse>();
  const [loading, setLoading] = useState(false);

  const updateLoading = (state: boolean) => {
    setLoading(state);
  };

  const prepareDataSnapshot = () =>
    advancedSearchApi
      .prepareDataSnapshot(advancedSearch, sortColumn, sortDirection)
      .then((res) => {
        setSnapshotObj(res.data);
        setExportDialogOpen(true);
      })
      .catch(handleHttpError);

  const handleAdvancedSearchSubmit = (search: TestRequestAdvancedSearchRQ) => {
    const formattedSearchObj = formatAdvancedSearchObject(search);
    setAdvancedSearch(formattedSearchObj);
    setSearchObjectAdvancedArchive(search);
  };

  const updateExcelConfiguratorTableOpen = (state: boolean) => {
    setExcelConfiguratorTableOpen(state);
  };

  useEffect(() => {
    // timeout to avoid UI flickering on dialog close
    if (!exportDialogOpen) {
      setTimeout(() => setExcelConfiguratorTableOpen(false), 500);
    }
  }, [exportDialogOpen]);

  return (
    <div className={classes.root}>
      <ExcelExportDialog
        loading={loading}
        updateLoading={updateLoading}
        snapshotObj={snapshotObj}
        excelConfiguratorTableOpen={excelConfiguratorTableOpen}
        setExcelConfiguratorTableOpen={updateExcelConfiguratorTableOpen}
        open={exportDialogOpen}
        onClose={() => setExportDialogOpen(false)}
      />
      <h2>ARCHIVE SEARCH</h2>
      <div>
        <AdvancedSearch
          setStoredSearchObject={setSearchObjectAdvancedArchive}
          resetStoredSearchObject={resetSearchObjectAdvancedArchive}
          onSubmit={handleAdvancedSearchSubmit}
          initialValues={advancedSearch as AdvancedSearchForm}
        />
      </div>
      {advancedSearch && (
        <>
          <Box height={theme.spacing(5)} />
          <h3 className={classes.withButton}>Results</h3>
          {exportArchivedSearch && hasResults && (
            <CustomButton
              disabled={loading}
              className={classes.exportResult}
              onClick={prepareDataSnapshot}
              text={loading ? "Export in progress..." : "Export"}
            />
          )}
          <TestRequestsResultView
            viewMode={ViewMode.Table}
            advancedSearch={advancedSearch}
            searchArchive
            updateHasResults={(newHasResults: boolean) => {
              setHasResults(newHasResults);
            }}
            updateSearchParameter={(newSortColumn: TestRequestSortColumn, newSortDirection: SortDirection) => {
              setSortColumn(newSortColumn);
              setSortDirection(newSortDirection);
            }}
          />
        </>
      )}
      <CustomFooterGraphic />
    </div>
  );
};

export default SearchPage;
